import React, { useState, useMemo, useContext } from 'react';
import { ExceptionStatus } from '@amzn/aet-dynamo-kit';
import Row from '@amzn/meridian/row';
import Column from '@amzn/meridian/column';
import Box from '@amzn/meridian/box';
import { FormattedMessage } from 'react-intl';
import Table, { TableRow, TableCell } from '@amzn/meridian/table';
import { TableSortDirection } from '@amzn/meridian/table/table';
import Button from '@amzn/meridian/button';
import Pagination from '@amzn/meridian/pagination';
import Select, { SelectOption } from '@amzn/meridian/select';
import Text from '@amzn/meridian/text';
import { OutlierException } from '../../types/types';
import { OutlierExceptionStatusTag } from '../OutlierExceptionStatusTag';
import {
  DEFAULT_PAGE_SIZE,
  PAGE_SIZE_OPTIONS,
  NUM_PAGES_BEFORE_SKIP_ARROW,
  MIN_NUM_PAGES_FOR_PAGINATION,
} from '../../types/constants';
import { StateContext } from '../../../../GlobalStateStore';
import { isFeatureEnabled, Feature } from '../../../../config/FeaturesConfig';
import ContactListeningModal from '../../../ContactListening/ContactListeningModal';
import ContactListening from '../../../ContactListening/ContactListening';
import {
  ContactData,
  defaultContactData,
} from '../../../../api/ContactListening/schema';
import DataState from '../../../../Types/DataState';
import { QueryVariablesContactListeningData } from '../../../../api/Naws';

interface OMExceptionsTableProps {
  data: OutlierException[];
  sortColumn: string;
  sortDirection: TableSortDirection;
  exceptionProcessingInProgress: boolean;
  onSort: (params: {
    sortColumn: string;
    sortDirection: TableSortDirection;
  }) => void;
  onApprove: (
    userId: string,
    deliveredToUsername: string,
    contactId: string,
    outlierCalYear: string
  ) => void;
  onReject: (
    userId: string,
    deliveredToUsername: string,
    contactId: string,
    outlierCalYear: string
  ) => void;
}

export const OMExceptionsTable: React.FC<OMExceptionsTableProps> = ({
  data,
  sortColumn,
  sortDirection,
  exceptionProcessingInProgress,
  onSort,
  onApprove,
  onReject,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);

  const globalState = useContext(StateContext);
  const { inspireOptions } = globalState.state;
  const { isTm, isOm, requesterUserID, requesterUsername } =
    inspireOptions.data;

  const contactListeningEnabled = isFeatureEnabled(Feature.CONTACT_LISTENING);
  const contactLinksEnabled = isFeatureEnabled(Feature.CONTACT_LINKS);
  const [contactListeningData, setContactListeningData] =
    useState<ContactData>(defaultContactData);
  const [
    contactListeningDataLoadingState,
    setContactListeningDataLoadingState,
  ] = useState<string>(DataState.INITIALIZED);
  const [contactListeningParams, setContactListeningParams] =
    useState<QueryVariablesContactListeningData>();
  const [recordingContactId, setRecordingContactId] = useState('');
  const [showRecordingModal, setShowRecordingModal] = useState(false);

  const commonClickstreamPattern = {
    'data-csa-c-profile-id': requesterUserID,
    'data-csa-c-username': requesterUsername,
    'data-csa-c-is-TM': isTm,
    'data-csa-c-is-OM': isOm,
  };

  const CLClickstream = {
    ...commonClickstreamPattern,
    slotID:
      'om-outlier-management-invalid-contact-open-contact-listening-modal',
    tag1: 'Outlier',
    tag2: 'Outlier Exceptions',
    tag3: '',
    tag4: '',
  };

  const sortedData = useMemo(() => {
    return [...data].sort((a, b) => {
      switch (sortColumn) {
        case 'contactId':
          return sortDirection === 'ascending'
            ? a.contactId.localeCompare(b.contactId)
            : b.contactId.localeCompare(a.contactId);
        case 'tmAlias':
          return sortDirection === 'ascending'
            ? a.tmAlias.localeCompare(b.tmAlias)
            : b.tmAlias.localeCompare(a.tmAlias);
        case 'csaAlias':
          return sortDirection === 'ascending'
            ? a.csaAlias.localeCompare(b.csaAlias)
            : b.csaAlias.localeCompare(a.csaAlias);
        case 'category':
          return sortDirection === 'ascending'
            ? a.category.localeCompare(b.category)
            : b.category.localeCompare(a.category);
        case 'justification':
          const aJustification = a.justification || '';
          const bJustification = b.justification || '';
          return sortDirection === 'ascending'
            ? aJustification.localeCompare(bJustification)
            : bJustification.localeCompare(aJustification);
        default:
          return 0;
      }
    });
  }, [data, sortColumn, sortDirection]);

  const { paginatedData, totalPages } = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const paginatedData = sortedData.slice(startIndex, startIndex + pageSize);
    const totalPages = Math.ceil(data.length / pageSize);
    return { paginatedData, totalPages };
  }, [sortedData, currentPage, pageSize, data.length]);

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1);
  };

  const handleSort = (params: {
    sortColumn: string;
    sortDirection: TableSortDirection;
  }) => {
    setCurrentPage(1);
    onSort(params);
  };

  const enableContactListeningModal =
    contactLinksEnabled || contactListeningEnabled;

  return (
    <Column spacing='medium'>
      <ContactListeningModal
        loadingState={contactListeningDataLoadingState}
        showModal={showRecordingModal}
        setShowModal={setShowRecordingModal}
        contactId={recordingContactId}
        contactListeningData={contactListeningData}
        contactListeningParams={contactListeningParams}
        CLClickstream={{
          ...CLClickstream,
          tag3: 'Contact Listening Modal',
        }}
      />
      <Table
        headerRows={1}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onSort={handleSort}
        showDividers={true}
        showStripes={true}
        spacing='small'
      >
        <TableRow>
          <TableCell sortColumn='contactId'>
            <FormattedMessage
              id='cs_perfportal_contact_id'
              defaultMessage='Contact ID'
            />
          </TableCell>
          <TableCell sortColumn='tmAlias'>
            <FormattedMessage id='cs_perfportal_tm_alias' defaultMessage='TM' />
          </TableCell>
          <TableCell sortColumn='csaAlias'>
            <FormattedMessage
              id='cs_perfportal_csa_alias'
              defaultMessage='Associate'
            />
          </TableCell>
          <TableCell sortColumn='category'>
            <FormattedMessage
              id='cs_perfportal_category'
              defaultMessage='Category'
            />
          </TableCell>
          <TableCell sortColumn='justification'>
            <FormattedMessage
              id='cs_perfportal_justification'
              defaultMessage='Justification'
            />
          </TableCell>
          <TableCell>
            <FormattedMessage
              id='cs_perfportal_actions'
              defaultMessage='Actions'
            />
          </TableCell>
        </TableRow>
        {paginatedData.map((exception: OutlierException) => {
          const isAcceptedException =
            exception.status === ExceptionStatus.ACCEPTED;
          const isDeniedException = exception.status === ExceptionStatus.DENIED;
          const isPendingException =
            exception.status === ExceptionStatus.PENDING;

          return (
            <TableRow key={exception.exceptionId}>
              <TableCell>
                {enableContactListeningModal ? (
                  <ContactListening
                    contactID={exception.contactId}
                    setContactListening={setContactListeningData}
                    setContactListeningState={
                      setContactListeningDataLoadingState
                    }
                    setContactListeningParams={setContactListeningParams}
                    setRecordingContactId={setRecordingContactId}
                    setShowRecordingModal={setShowRecordingModal}
                    clickstreamTags={{
                      'data-csa-c-slot-id':
                        'om-outlier-management-invalid-contact-contact-id-slot',
                      'data-csa-c-content-id': `om-outlier-management-invalid-contact-link-for-${exception.contactId}`,
                      'data-csa-c-tag1': CLClickstream.tag1,
                      'data-csa-c-tag2': CLClickstream.tag2,
                      'data-csa-c-contactid': exception.contactId,
                    }}
                    // TODO: Remove cscEndpoint from anywhere it is used in front end: https://sim.amazon.com/issues/CSKoala-2545
                    cscEndpoint={''} // We don't need to provide CSC link anymore as CSC is already deprecated:
                    contactHandlerId={exception.csaUserIdWithPrefix}
                    CLClickstream={CLClickstream}
                  />
                ) : (
                  exception.contactId
                )}
              </TableCell>
              <TableCell>{exception.tmAlias}</TableCell>
              <TableCell>{exception.csaAlias}</TableCell>
              <TableCell>{exception.category}</TableCell>
              <TableCell>{exception.justification || '-'}</TableCell>
              <TableCell>
                <Row spacing='350'>
                  {(isAcceptedException || isDeniedException) && (
                    <Box>
                      <OutlierExceptionStatusTag status={exception.status} />
                    </Box>
                  )}
                  {isPendingException && (
                    <>
                      <Button
                        size='small'
                        onClick={() =>
                          onApprove(
                            exception.omUserIdWithPrefix, //OM userId (with CSC prefix)
                            exception.csaAlias, //deliveredToUsername
                            exception.contactId, // contactId
                            exception.outlierCalYear // outlierCalYear
                          )
                        }
                        type='secondary'
                        disabled={exceptionProcessingInProgress}
                        minWidth={75}
                        data-csa-c-type='button'
                        data-csa-c-slot-id='om-outlier-management-invalid-contact-approve-button-slot'
                        data-csa-c-tag1='Coaching'
                        data-csa-c-tag2='Invalid Contacts'
                        data-csa-c-tag3={exception.tmAlias}
                        data-csa-c-content-id={exception.contactId}
                        {...commonClickstreamPattern}
                      >
                        <FormattedMessage
                          id='cs_perfportal_approve'
                          defaultMessage='Approve'
                        />
                      </Button>
                      <Button
                        size='small'
                        onClick={() =>
                          onReject(
                            exception.omUserIdWithPrefix, //OM userId (with CSC prefix)
                            exception.csaAlias, //deliveredToUsername
                            exception.contactId, // contactId
                            exception.outlierCalYear // outlierCalYear
                          )
                        }
                        type='secondary'
                        disabled={exceptionProcessingInProgress}
                        minWidth={75}
                        data-csa-c-type='button'
                        data-csa-c-slot-id='om-outlier-management-invalid-contact-reject-button-slot'
                        data-csa-c-tag1='Coaching'
                        data-csa-c-tag2='Invalid Contacts'
                        data-csa-c-tag3={exception.tmAlias}
                        data-csa-c-content-id={exception.contactId}
                        {...commonClickstreamPattern}
                      >
                        <FormattedMessage
                          id='cs_perfportal_reject'
                          defaultMessage='Reject'
                        />
                      </Button>
                    </>
                  )}
                </Row>
              </TableCell>
            </TableRow>
          );
        })}
      </Table>

      <Row
        alignmentHorizontal='justify'
        alignmentVertical='center'
        spacingInset='300 none'
      >
        <Row
          spacing='small'
          alignmentVertical='center'
          spacingInset='none none none 500'
        >
          <Text type='b200'>
            <FormattedMessage
              id='cs_perfportal_results_per_page'
              defaultMessage='Results per page'
            />
          </Text>
          <Row spacingInset='none 300 none 300'>
            <Select
              value={pageSize}
              onChange={handlePageSizeChange}
              width={75}
              size='small'
            >
              {PAGE_SIZE_OPTIONS.map(size => (
                <SelectOption key={size} value={size} label={size.toString()} />
              ))}
            </Select>
          </Row>
        </Row>

        {totalPages > MIN_NUM_PAGES_FOR_PAGINATION && (
          <Pagination
            numberOfPages={totalPages}
            currentPage={currentPage}
            onChange={setCurrentPage}
            showSkipArrows={totalPages > NUM_PAGES_BEFORE_SKIP_ARROW}
          />
        )}
      </Row>
    </Column>
  );
};

export default OMExceptionsTable;
